import React from 'react';
import Helmet from 'react-helmet';
import { Formik } from 'formik';
import { Elements as StripeElements } from '@stripe/react-stripe-js';

import RegistrationForm from './components/RegistrationForm';
import RegistrationPayment from './components/RegistrationPayment';
import useStripePromise from '../../../../hooks/useStripePromise';
import useWeddingsGift from './useWeddingsGift';
import { WEDDINGS_GIFT_STEPS } from './constants';
import SignupAlbumLayout from '../../../../components/global/SignupAlbumLayout';
import RegistrationPhoneVerify from './components/RegistrationPhoneVerify';
import RegistrationSetupGallery from './components/RegistrationSetupGallery';
import RegistrationSendGiftForm from './components/RegistrationSendGiftForm';
import Button from '../../../../components/global/Button';
import Angle from '../../../../components/global/Angle';
import SendWeddingsGiftSuccess from './components/SendWeddingsGiftSuccess';
import RegistrationEventItemSelection from '../WeddingsRegistration/components/RegistrationEventItemSelection';
import CreateWizardLayout from '../../../pro/components/CreateWizardLayout';
import { hasMissingFields } from './helpers';
import { formatPhone } from '../../../../util/phone';

const WeddingsGift = props => {
  const stripePromise = useStripePromise();
  const {
    step,
    eventItems,
    onPhoneVerificationRequest,
    onPinVerify,
    onPinCodeResend,
    onConfigureGallery,
    onSendGiftNow,
    onTransferEvent,
    onCheckout,
    loading,
    onEventItemSelected,
    canGoBack,
    onGoBack,
    defaultPromoCode,
    eventItem,
  } = useWeddingsGift();

  const onSubmit = values => {
    if (step === WEDDINGS_GIFT_STEPS.DETAILS) {
      onPhoneVerificationRequest(values);
    }

    if (step === WEDDINGS_GIFT_STEPS.PIN_VERIFY) {
      onPinVerify(values);
    }

    if (step === WEDDINGS_GIFT_STEPS.SEND_GIFT_NOW) {
      onTransferEvent(values);
    }

    if (step === WEDDINGS_GIFT_STEPS.CHECKOUT) {
      onCheckout(values);
    }
  };

  const title = values => {
    if (step === WEDDINGS_GIFT_STEPS.PIN_VERIFY) {
      return (
        <h3>
          Please enter the code that was sent to: <br />
          <span className="text-primary">
            {formatPhone(values.phone_number)}
          </span>{' '}
        </h3>
      );
    }

    if (step === WEDDINGS_GIFT_STEPS.SKU_SELECTION) {
      return <h2>Pick a plan</h2>;
    }
    if (step === WEDDINGS_GIFT_STEPS.SETUP_GALLERY) {
      return <h2>Select your next step</h2>;
    }
    if (step === WEDDINGS_GIFT_STEPS.CHECKOUT) {
      return <h2>Your payment details</h2>;
    }
    if (step === WEDDINGS_GIFT_STEPS.SEND_GIFT_NOW) {
      return <h2>Send gift now!</h2>;
    }
    if (step === WEDDINGS_GIFT_STEPS.SEND_GIFT_NOW_SUCCESS) {
      return <h2>Your gift is on the way!</h2>;
    }

    return (
      <h2>
        WaldoWed makes the perfect gift! <br />
        Let&apos;s get started!
      </h2>
    );
  };

  const buttonLabel = () => {
    if (loading) {
      return 'Loading...';
    }

    if (step === WEDDINGS_GIFT_STEPS.SEND_GIFT_NOW) {
      return 'Transfer this event';
    }

    return 'Continue';
  };

  return (
    <StripeElements stripe={stripePromise}>
      <Helmet>
        <title>WaldoWed makes the perfect wedding gift!</title>
      </Helmet>

      <SignupAlbumLayout.Wrapper logoSrc="/waldowed-logo.svg" withCTA={false}>
        <div className="weddings-gift-wrapper">
          <Formik
            onSubmit={onSubmit}
            initialValues={{
              first_name: '',
              last_name: '',
              phone_number: '+1',
              code: '',
              relationship: '',
              groom_name: '',
              bride_name: '',
              couples_phone_number: '+1',
              couple_email_address: '',
              terms_checked: false,
              promo_code: '',
              wedding_date: null,
              default_promo_code: defaultPromoCode ?? '',
              event_item_id: props.eventItemId || '',

              // Checkout logic
              stripe_token: null,
            }}
            validateOnBlur={false}
            validateOnChange={false}
            render={({
              values,
              handleSubmit,
              handleChange,
              handleBlur,
              errors,
              touched,
            }) => {
              const selectedWeddingEventItem = eventItems.find(
                item => item.id === values.event_item_id,
              );

              return (
                <>
                  <div className="mt-3">{title(values)}</div>
                  {step === WEDDINGS_GIFT_STEPS.DETAILS && (
                    <RegistrationForm
                      values={values}
                      handleSubmit={handleSubmit}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      errors={errors}
                      touched={touched}
                      loading={loading}
                    />
                  )}

                  {step === WEDDINGS_GIFT_STEPS.PIN_VERIFY && (
                    <RegistrationPhoneVerify
                      loading={loading}
                      handleChange={handleChange}
                      onPinCodeResend={onPinCodeResend}
                      values={values}
                    />
                  )}

                  {step === WEDDINGS_GIFT_STEPS.SKU_SELECTION && eventItems && (
                    <RegistrationEventItemSelection
                      values={values}
                      handleChange={handleChange}
                      eventItems={eventItems}
                      onEventItemSelected={onEventItemSelected}
                      errors={errors}
                      touched={touched}
                    />
                  )}

                  {step === WEDDINGS_GIFT_STEPS.SETUP_GALLERY && (
                    <RegistrationSetupGallery
                      onConfigureGallery={onConfigureGallery}
                      onSendGiftNow={onSendGiftNow}
                    />
                  )}

                  {step === WEDDINGS_GIFT_STEPS.SEND_GIFT_NOW && (
                    <RegistrationSendGiftForm
                      loading={loading}
                      handleSubmit={handleSubmit}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      values={values}
                      eventItem={eventItem}
                      errors={errors}
                      touched={touched}
                    />
                  )}

                  {step === WEDDINGS_GIFT_STEPS.CHECKOUT &&
                    selectedWeddingEventItem && (
                      <RegistrationPayment
                        values={values}
                        submitting={loading}
                        onCheckout={onSubmit}
                        handleChange={handleChange}
                        eventItem={selectedWeddingEventItem}
                      />
                    )}

                  {step === WEDDINGS_GIFT_STEPS.SEND_GIFT_NOW_SUCCESS && (
                    <SendWeddingsGiftSuccess
                      eventItem={eventItem}
                      values={values}
                    />
                  )}
                  <CreateWizardLayout.StickyFooter className="wedding-gift-sticky-footer">
                    <div className="d-flex">
                      {canGoBack && (
                        <Button
                          type="button"
                          btnStyle="link"
                          data-testid="album-create-wizard-prev-btn"
                          onClick={onGoBack}
                          className="mr-auto text-extra-bold"
                        >
                          <Angle direction="left" className="mr-1" /> Back
                        </Button>
                      )}
                    </div>

                    <div className="d-flex justify-content-end">
                      <Button
                        type="submit"
                        onClick={handleSubmit}
                        disabled={loading || hasMissingFields(step, values)}
                      >
                        {buttonLabel(values)}
                      </Button>
                    </div>
                  </CreateWizardLayout.StickyFooter>
                </>
              );
            }}
          />
        </div>
      </SignupAlbumLayout.Wrapper>
    </StripeElements>
  );
};

export default WeddingsGift;
